import { useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Upload, Loader2 } from "lucide-react";
import { MailNowOffer } from "@/components/MailNowOffer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RESULT_CLS = { verified: "text-emerald-700", corrected: "text-emerald-700", failed: "text-red-600", not_found: "text-amber-700", skipped: "text-slate-500", unchecked: "text-red-600" };

export const AddressImport = ({ onDone }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const download = () => {
    fetch(`${API}/admin/address-fix/template.csv`, { headers: { Authorization: `Bearer ${localStorage.getItem("elite_token")}` } })
      .then((r) => r.blob())
      .then((b) => { const u = URL.createObjectURL(b); const a = document.createElement("a"); a.href = u; a.download = "flagged_mailing_addresses.csv"; a.click(); URL.revokeObjectURL(u); });
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/address-fix/import", { csv_data: await f.text() });
      setRes(data);
      toast.success(`${data.summary.fixed} of ${data.summary.rows} addresses fixed`);
      onDone?.();
    } catch (x) { toast.error(apiError(x)); }
    setBusy(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={download} data-testid="address-template-btn"><Download className="mr-1.5 h-4 w-4" /> Flagged CSV</Button>
      <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="address-import-btn">
        {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />} {busy ? "Verifying…" : "Upload corrections"}
      </Button>
      <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={upload} data-testid="address-import-input" />
      <Dialog open={!!res} onOpenChange={(o) => !o && setRes(null)}>
        <DialogContent className="max-w-2xl" data-testid="address-import-results">
          <DialogHeader>
            <DialogTitle>Address import results</DialogTitle>
            <DialogDescription data-testid="address-import-summary">
              {res && `${res.summary.rows} rows · ${res.summary.fixed} fixed · ${res.summary.failed} still failing · ${res.summary.not_found} not found · ${res.summary.skipped} skipped`}
            </DialogDescription>
          </DialogHeader>
          {res && <MailNowOffer key={res.results.length + res.summary.fixed} testId="import-mail-now"
            ids={res.results.filter((r) => ["verified", "corrected"].includes(r.result)).map((r) => r.lead_id)} onDone={onDone} />}
          <div className="max-h-[60vh] overflow-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-white text-left uppercase tracking-wider text-slate-400"><tr><th className="py-1.5 pr-2">Row</th><th className="py-1.5 pr-2">Lead</th><th className="py-1.5">Result</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {res?.results.map((r) => (
                  <tr key={r.row} data-testid={`address-import-row-${r.row}`}>
                    <td className="py-1.5 pr-2 align-top text-slate-500">{r.row}</td>
                    <td className="py-1.5 pr-2 align-top font-semibold text-slate-800">{r.name || r.lead_id}</td>
                    <td className="py-1.5 align-top">
                      <span className={`font-semibold capitalize ${RESULT_CLS[r.result] || ""}`}>{r.result.replace("_", " ")}</span>
                      {r.standardized && <div className="text-slate-600">{[r.standardized.line1, r.standardized.line2, r.standardized.city, `${r.standardized.state} ${r.standardized.zip}`].filter(Boolean).join(", ")}</div>}
                      {r.errors?.length > 0 && !r.standardized && <div className="text-red-600">{r.errors.join("; ")}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
